import { Injectable, OnDestroy } from '@angular/core';
import { Subject, fromEvent, merge, Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CommonService } from '../../shared/service/common.service';
import { logout } from 'src/app/store/user/user.actions';
import { Actions, ofType } from '@ngrx/effects';

@Injectable({ providedIn: 'root' })
export class AlertSseService implements OnDestroy {
  private worker?: Worker;

  private readonly alertSubject = new Subject<any>();
  private readonly notificationStatusSubject = new Subject<any>();

  alerts$ = this.alertSubject.asObservable();
  notificationStatus$ = this.notificationStatusSubject.asObservable();

  private idleSub?: Subscription;
  private logoutSub?: Subscription;
  private storageSub?: Subscription;
  private visibilitySub?: Subscription;

  private lastUrl?: string;
  private lastHeaders?: Record<string, string>;

  private sseBlockedUntil = 0;
  private readonly COOLDOWN_MS = 3000;
  private readonly IDLE_TIME = 10 * 60 * 1000;

  constructor(
    private readonly commonService: CommonService,
    private readonly actions$: Actions
  ) {
    this.listenToLogout();
    this.listenToStorage();
  }

  ngOnDestroy() {
    this.cleanup();
  }

  cleanup() {
    this.stop();
    this.logoutSub?.unsubscribe();
    this.storageSub?.unsubscribe();
    this.visibilitySub?.unsubscribe();
    this.alertSubject.complete();
    this.notificationStatusSubject.complete();
  }

  start(url: string, headers?: Record<string, string>) {
    this.resetCooldown();

    this.lastUrl = url;
    this.lastHeaders = headers;

    this.ensureWorker();
    this.worker!.postMessage({ type: 'START', payload: { url, headers } });

    this.startIdleWatcher();
  }

  stop() {
    this.stopIdleWatcher();
    this.terminateWorker();
  }

  resetState() {
    this.stop();
    this.lastUrl = undefined;
    this.lastHeaders = undefined;
    this.resetCooldown();
  }

  needsReconnection(): boolean {
    return this.canReconnect();
  }

  reconnectOnActivity() {
    if (!this.canReconnect()) return;

    setTimeout(() => {
      const token = this.commonService.getAccessToken();
      if (!token) return;
      if (!this.canReconnect()) return;

      this.start(this.lastUrl!, {
        ...this.lastHeaders,
        authorization: token,
      });
    }, 500);
  }

  setNotificationStatus(status: {
    hasPermission: boolean;
    isSubscribed: boolean;
  }) {
    this.notificationStatusSubject.next(status);
  }

  private ensureWorker() {
    if (this.worker) return;

    this.worker = new Worker(
      new URL('../../worker/alert-sse.worker', import.meta.url)
    );

    this.worker.onmessage = ({ data }) => this.handleWorkerMessage(data);
  }

  private handleWorkerMessage(data: any) {
    switch (data.type) {
      case 'ALERT_EVENT':
        this.alertSubject.next(data.data);
        break;

      case 'ERROR':
        if (this.isUnauthorized(data)) {
          this.enterCooldown();
          this.stop();
        }
        break;

      case 'CLOSED':
        break;
    }
  }

  private terminateWorker() {
    if (!this.worker) return;

    this.worker.postMessage({ type: 'STOP' });
    this.worker.terminate();
    this.worker = undefined;
  }

  private canReconnect(): boolean {
    return !!this.lastUrl && !this.worker && Date.now() >= this.sseBlockedUntil;
  }

  private enterCooldown() {
    this.sseBlockedUntil = Date.now() + this.COOLDOWN_MS;
  }

  private resetCooldown() {
    this.sseBlockedUntil = 0;
  }

  private isUnauthorized(data: any): boolean {
    return data.status === 401 || data?.error?.includes('401');
  }

  private startIdleWatcher() {
    if (this.idleSub) return;

    const activity$ = merge(
      fromEvent(globalThis, 'click'),
      fromEvent(globalThis, 'keydown'),
      fromEvent(globalThis, 'mousemove'),
      fromEvent(globalThis, 'scroll')
    );

    this.idleSub = activity$
      .pipe(switchMap(() => timer(this.IDLE_TIME)))
      .subscribe(() => this.stop());
  }

  private stopIdleWatcher() {
    this.idleSub?.unsubscribe();
    this.idleSub = undefined;
  }

  private listenToLogout() {
    this.logoutSub = this.actions$
      .pipe(ofType(logout))
      .subscribe(() => this.forceStop());
  }

  private listenToStorage() {
    this.storageSub = fromEvent<StorageEvent>(globalThis, 'storage').subscribe(
      (e) => {
        if (e.key === 'token' && !e.newValue) this.forceStop();
      }
    );

    this.visibilitySub = fromEvent(document, 'visibilitychange').subscribe(
      () => {
        if (
          !document.hidden &&
          !this.commonService.getlocalStorageItem('token')
        ) {
          this.forceStop();
        }
      }
    );
  }

  private forceStop() {
    this.stop();
    this.lastUrl = undefined;
    this.lastHeaders = undefined;
    this.sseBlockedUntil = Date.now() + 5000;
  }
}
